import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "parentCheckbox",
    "childCheckbox",
    "allToggle",
    "activityCheckbox",
    "trigger",
    "popover",
    "displayButton",
    "displayValue",
    "expanded",
    "parentPill",
    "childrenRow",
    "parentsRow",
    "parentCount",
  ];
  static values = {
    singular: String,
    plural: String,
    selectedFormat: String,
  };

  connect() {
    this.updateCount();
    this.updateToggleButtons();
    // Start in collapsed state if we have display button (mobile component)
    if (this.hasDisplayButtonTarget) {
      this.updateDisplay();
    }
  }

  expand() {
    if (!this.hasDisplayButtonTarget) return;

    this.displayButtonTarget.classList.add("hidden");
    this.expandedTarget.classList.remove("hidden");
    this.expandedTarget.classList.add("flex");

    // Focus the expanded area so focusout events work
    setTimeout(() => {
      this.expandedTarget.focus();
    }, 100);
  }

  showChildren(event) {
    if (!this.hasChildrenRowTarget) return;

    const button = event.currentTarget;
    const parentId = button.dataset.parentId;

    // Hide all children rows
    this.childrenRowTargets.forEach((row) => {
      row.classList.add("hidden");
      row.classList.remove("flex");
    });

    // Remove active state from all parent pills
    this.parentPillTargets.forEach((pill) => {
      pill.classList.remove("border-[#9565fe]", "text-[#9565fe]", "bg-[#ebe2ff]");
    });

    // Show the selected parent's children
    const targetRow = this.childrenRowTargets.find(
      (row) => row.dataset.parentId === parentId
    );
    if (targetRow) {
      targetRow.classList.remove("hidden");
      targetRow.classList.add("flex");
      // Expand dropdown to wide state and add border to parents column
      if (this.hasExpandedTarget) {
        this.expandedTarget.classList.add("md:!w-[600px]");
        if (this.hasParentsRowTarget) {
          this.parentsRowTargets[0].classList.add("md:flex-[0_0_calc(50%-0.5rem)]", "md:pr-4", "md:border-r", "md:border-[#e0e0e0]");
        }
      }
    }

    // Add active state to clicked parent
    button.classList.add("border-[#9565fe]", "text-[#9565fe]", "bg-[#ebe2ff]");
  }

  collapse(event) {
    if (!this.hasDisplayButtonTarget) return;

    // Check if the new focus target is still within the expanded area
    if (event && event.relatedTarget && this.expandedTarget.contains(event.relatedTarget)) {
      return; // Don't collapse if focus is moving to another element within the component
    }

    // Delay collapse slightly to allow for clicks within the component
    this.collapseTimeout = setTimeout(() => {
      // Hide all children rows and reset to narrow state
      if (this.hasChildrenRowTarget) {
        this.childrenRowTargets.forEach((row) => {
          row.classList.add("hidden");
          row.classList.remove("flex");
        });
      }
      // Remove wide state class and border from parents column
      this.expandedTarget.classList.remove("md:!w-[600px]");
      if (this.hasParentsRowTarget) {
        this.parentsRowTargets[0].classList.remove("md:flex-[0_0_calc(50%-0.5rem)]", "md:pr-4", "md:border-r", "md:border-[#e0e0e0]");
      }
      this.expandedTarget.classList.add("hidden");
      this.expandedTarget.classList.remove("flex");
      this.displayButtonTarget.classList.remove("hidden");
      this.updateDisplay();

      // Notify that dropdown closed (for hero search transition)
      window.dispatchEvent(new CustomEvent("activity-selector:closed"));
    }, 150);
  }

  cancelCollapse() {
    if (this.collapseTimeout) {
      clearTimeout(this.collapseTimeout);
      this.collapseTimeout = null;
    }
  }

  updateDisplay() {
    if (!this.hasDisplayValueTarget) return;

    // Only count activity checkboxes, not parent checkboxes
    const count = this.hasActivityCheckboxTarget
      ? this.activityCheckboxTargets.filter((cb) => cb.checked).length
      : 0;

    if (count === 0) {
      this.displayValueTarget.textContent = "";
      this.displayValueTarget.classList.add("hidden");
    } else {
      const text = this.selectedFormatValue.replace("%{count}", count);
      this.displayValueTarget.textContent = text;
      this.displayValueTarget.classList.remove("hidden");
    }
  }

  updateCount() {
    // Only count activity checkboxes, not parent checkboxes
    const selected = this.hasActivityCheckboxTarget
      ? this.activityCheckboxTargets.filter((cb) => cb.checked).length
      : 0;

    // Update legacy trigger if it exists
    if (this.hasTriggerTarget) {
      if (selected > 0) {
        this.triggerTarget.textContent = `${selected} ${
          selected > 1 ? this.pluralValue : this.singularValue
        }`;
      } else {
        this.triggerTarget.textContent = this.triggerTarget.dataset.defaultLabel;
      }
    }

    // Update mobile display
    if (this.hasDisplayValueTarget) {
      this.updateDisplay();
    }

    this.updateToggleButtons();
    this.updateParentCounts();

    // Emit sync event with all selected activity IDs
    const selectedIds = this.hasActivityCheckboxTarget
      ? this.activityCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      : [];

    window.dispatchEvent(new CustomEvent("search-field:changed", {
      detail: {
        field: "activities",
        value: selectedIds,
        source: this.element
      }
    }));
  }

  toggleChildren(event) {
    const parentCheckbox = event.target;
    const parentId = parentCheckbox.value;
    const childCheckboxes = this.element.querySelectorAll(
      `input[data-parent-id="${parentId}"]`,
    );

    childCheckboxes.forEach((checkbox) => {
      checkbox.checked = parentCheckbox.checked;
    });
  }

  updateParent(event) {
    const childCheckbox = event.target;
    const parentId = childCheckbox.dataset.parentId;

    // Find parent checkbox by value and parentCheckbox target
    const parentCheckbox = this.parentCheckboxTargets.find(
      (cb) => cb.value === parentId
    );

    if (parentCheckbox) {
      const siblingCheckboxes = this.element.querySelectorAll(
        `input[data-parent-id="${parentId}"]`,
      );
      const checkedSiblings = Array.from(siblingCheckboxes).filter(
        (cb) => cb.checked,
      );

      // If all children are checked, check parent
      // Otherwise, uncheck parent
      if (checkedSiblings.length === siblingCheckboxes.length) {
        parentCheckbox.checked = true;
      } else {
        parentCheckbox.checked = false;
      }
    }
  }

  toggleAllChildren(event) {
    this.cancelCollapse(); // Prevent collapse when clicking toggle button
    const button = event.currentTarget;
    const parentId = button.dataset.parentId;
    const childCheckboxes = this.element.querySelectorAll(
      `input[data-parent-id="${parentId}"]`,
    );

    const allChecked = Array.from(childCheckboxes).every((cb) => cb.checked);

    childCheckboxes.forEach((checkbox) => {
      checkbox.checked = !allChecked;
    });

    // Update button text
    const buttonText = button.querySelector('span');
    if (buttonText) {
      buttonText.textContent = allChecked
        ? button.dataset.selectAllText
        : button.dataset.deselectAllText;
    } else {
      button.textContent = allChecked
        ? button.dataset.selectAllText
        : button.dataset.deselectAllText;
    }

    // Update the hidden parent checkbox
    const parentCheckbox = this.parentCheckboxTargets.find(
      (cb) => cb.value === parentId
    );
    if (parentCheckbox) {
      parentCheckbox.checked = !allChecked;
    }

    this.updateCount();
  }

  updateParentCounts() {
    if (!this.hasParentCountTarget) return;

    this.parentCountTargets.forEach((badge) => {
      const parentId = badge.dataset.parentId;
      const childCheckboxes = this.element.querySelectorAll(
        `input[data-parent-id="${parentId}"]`,
      );
      const checkedCount = Array.from(childCheckboxes).filter((cb) => cb.checked).length;

      if (checkedCount > 0) {
        badge.textContent = checkedCount;
        badge.classList.remove("hidden");
        badge.classList.add("flex");
      } else {
        badge.textContent = "";
        badge.classList.add("hidden");
        badge.classList.remove("flex");
      }
    });
  }

  updateToggleButtons() {
    if (!this.hasAllToggleTarget) return;

    this.allToggleTargets.forEach((button) => {
      const parentId = button.dataset.parentId;
      const childCheckboxes = this.element.querySelectorAll(
        `input[data-parent-id="${parentId}"]`,
      );
      const allChecked = Array.from(childCheckboxes).every((cb) => cb.checked);

      const buttonText = button.querySelector('span');
      if (buttonText) {
        buttonText.textContent = allChecked
          ? button.dataset.deselectAllText
          : button.dataset.selectAllText;
      } else {
        button.textContent = allChecked
          ? button.dataset.deselectAllText
          : button.dataset.selectAllText;
      }
    });
  }
}
